/**
 * Keeps the cart attribute `has_preorder_line` in sync with whether the cart
 * actually contains any pre-order line items. The attribute is read by Payfy
 * (or any payment-customization app that supports cart attributes) to hide
 * Shopify Payments when pre-order items are present, since Shopify Payments
 * only holds reservations for 7 days while pre-order deliveries are weeks or
 * months out.
 *
 * Source of truth for "is this cart pre-order?" is the server-rendered
 * `cart-validation-data` script, which evaluates each variant against the
 * Traede metafields via the `is-variant-preorder` Liquid snippet.
 */

const ATTRIBUTE_KEY = 'has_preorder_line'

async function fetchHasPreorderFromCartPage(): Promise<boolean> {
  try {
    const res = await fetch('/cart')
    const html = await res.text()
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const el = doc.getElementById('cart-validation-data')
    if (!el?.textContent) return false
    const data = JSON.parse(el.textContent)
    return !!data.has_preorder
  } catch {
    return false
  }
}

function readHasPreorderFromDom(): boolean {
  const el = document.getElementById('cart-validation-data')
  if (!el?.textContent) return false
  try {
    const data = JSON.parse(el.textContent)
    return !!data.has_preorder
  } catch {
    return false
  }
}

async function fetchCurrentAttribute(): Promise<string> {
  try {
    const res = await fetch('/cart.js')
    const cart = await res.json()
    return String(cart.attributes?.[ATTRIBUTE_KEY] || '')
  } catch {
    return ''
  }
}

export async function syncPreorderCartAttribute({
  refresh = false,
}: { refresh?: boolean } = {}) {
  const hasPreorder = refresh
    ? await fetchHasPreorderFromCartPage()
    : readHasPreorderFromDom()
  const desiredValue = hasPreorder ? 'true' : ''
  const currentValue = await fetchCurrentAttribute()

  if (currentValue === desiredValue) return

  try {
    await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { [ATTRIBUTE_KEY]: desiredValue },
      }),
    })
  } catch (e) {
    console.error('Failed to sync pre-order cart attribute:', e)
  }
}
